import { useEffect, useRef, useState } from 'react'
import { AgencyModel } from '../models/AgencyModel'
import FeedConfiguration from './models/FeedConfiguration'
import { RespondAppConfiguration } from './models/RespondAppConfiguration'
import { loadSavedFeeds, updateSavedFeeds } from './models/SavedFeeds'
import TimeDisplayOption, { timeDisplayOptionFromValue } from './models/TimeDisplayOptions'
import { filterCalls } from './models/Filtering'
import { fetchAgencies, fetchIncidents } from './models/WebAppRequests'
import { IncidentModel } from '../models/IncidentModel'
import React from 'react'
import ManageSavedFeeds from './components/ManageSavedFeeds'
import ConfigureFeed from './components/ConfigureFeed'
import { PPLoadingDots } from '../components/PPLoadingDots'
import { EmptyState } from '../components/EmptyState'
import { Flex, PrimaryButton } from '@workday/canvas-kit-react'
import { PrimaryButtonColors } from '../components/CanvasThemes'
import IncidentDetails from './components/IncidentDetails'
import DisplaySettings from './components/DisplaySettings'
import RespondMenuBar from './components/RespondMenuBar'
import IncidentMap, { IncidentMapViewport } from './components/IncidentMap'
import IncidentList from './components/IncidentList'
import { IncidentFeedModel } from '../models/IncidentFeedModel'
import { CognitoUserSession } from 'amazon-cognito-identity-js'
import { AGENCY } from '../models/models'
import { ScreenWidthCategory, UpdateConfirmationContext, UpdateToastContext, ScreenWidthCategoryContext } from '../utils'
import { RespondAppContextProviders } from './models/RespondAppContextProviders'

export type PanelSide = "left" | "right" 

const agencyForAgencyID = (agencyPool: AgencyModel[], agencyID: string) => {
  return agencyPool.find(agency => agency.agencyID === agencyID)!
}

const saveFeedIfNeeded = (configuration: FeedConfiguration) => {
  if (configuration.isValid() && configuration.type === 'SAVED') {
    saveFeed(configuration)
  }
}

const incidentFromURL = (agencies: AgencyModel[], params: URLSearchParams) => {
  const agencyIDList = params.get('agencies') ?? ''
  const incidentId = params.get('incident') ?? ''

  const selectAgency =
    agencyIDList.length > 0 && agencyIDList.split(',').length === 1
      ? agencyForAgencyID(agencies, agencyIDList.split(',')[0])
      : undefined

  return selectAgency && incidentId.length > 0
    ? {
        agency: selectAgency,
        incidentID: incidentId
      }
    : undefined
}

const saveFeed = (feedToSave: FeedConfiguration) => {
  /* 
   ALGORITHM:
   - Load current saved feeds
   - Filter out feedToSave if previously saved
   - Place feedToSave at start of new list
   - Remove any duplicate feeds in the list
   - Save list
   */
  let feedsToSave = [feedToSave, ...loadSavedFeeds().filter(feed => feed.id !== feedToSave.id)].filter(
    (feed, index, arr) => index === arr.findIndex(f => f.duplicates(feed, true))
  )
  updateSavedFeeds(feedsToSave)
}

const defaultFeed = (
  appConfiguration: RespondAppConfiguration,
  agencyFeed: FeedConfiguration,
  urlFeed: FeedConfiguration,
  v1CookieFeed: FeedConfiguration,
  savedFeeds: FeedConfiguration[]
) => {
  let defaultFeed: FeedConfiguration
  if (appConfiguration.context === 'ADMIN') {
    defaultFeed = agencyFeed
  } else if (urlFeed.isValid()) {
    defaultFeed = savedFeeds.find(feed => feed.duplicates(urlFeed, false)) ?? urlFeed
  } else if (savedFeeds.length > 0) {
    defaultFeed = savedFeeds[0]
  } else if (v1CookieFeed.isValid()) {
    defaultFeed = v1CookieFeed
  } else {
    defaultFeed = new FeedConfiguration(undefined, '', [], '', [], 'PRODUCTION', 'NEW', false, false, false)
  }
  return defaultFeed
}

export interface RespondAppCoreProps {
  appConfiguration: RespondAppConfiguration
  contextProviders: RespondAppContextProviders
  searchParams: URLSearchParams
  session?: CognitoUserSession
  sessionAgency?: AGENCY
  onSelectIncident: (incident: IncidentModel) => void
  onSelectAgency?: (agency: AgencyModel) => void
  onAgencyLoaded?: (agency: AgencyModel) => void
}

export const RespondAppCore = ({
  appConfiguration,
  contextProviders,
  searchParams,
  session,
  sessionAgency,
  onSelectIncident,
  onSelectAgency,
  onAgencyLoaded
}: RespondAppCoreProps) => {
  const isSmallScreenWidth = contextProviders.screenWidthCategory < ScreenWidthCategory.Large
  const [selectedTab, setSelectTab] = useState<number>(1)
  const [collapsedHeader, setCollapsedHeader] = useState<boolean>(false)
  const [userLocation, setUserLocation] = useState<{lat: number, lng: number}>()
  const defaultPanelSide = localStorage.getItem('IncidentListPanelSide') === "left" ? "left" : "right"
  const [panelSide, setPanelSide] = useState<PanelSide>(defaultPanelSide)

  const token = appConfiguration.context === 'SANDBOX_LINK' ? searchParams.get('sandbox-token') ?? '' : ''
  const audioRef = useRef<HTMLAudioElement>(null)
  const defaultTimeDisplayOption = timeDisplayOptionFromValue(localStorage.getItem('TimeDisplayOption'))
  const [agencies, setAgencies] = useState<AgencyModel[]>([])
  const [loadedData, setLoadedData] = useState<{ feedModel: IncidentFeedModel; agencyIDs: string[] }>({
    feedModel: IncidentFeedModel.modelFromJSON({}),
    agencyIDs: []
  })
  const mapViewport = useRef<IncidentMapViewport>()

  const [feedToConfigure, setFeedToConfigure] = useState<FeedConfiguration | null>(null)
  const [timeDisplay, setTimeDisplay] = useState<TimeDisplayOption>(defaultTimeDisplayOption)
  const [search, setSearch] = useState('')
  const [radioAgency, setRadioAgency] = useState<AgencyModel | null>(null)

  const hangleESCKey = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      setCollapsedHeader(false)
    }
  }

  const agencyFeed = new FeedConfiguration(
    undefined,
    sessionAgency?.agencyname ?? '',
    sessionAgency?.agencyid ? [sessionAgency.agencyid] : [],
    '',
    [],
    appConfiguration.allowedEnvironments[0],
    'NEW',
    false,
    false,
    false
  )
  const urlFeed = FeedConfiguration.fromURLSearch(searchParams)
  const v1CookieFeed = FeedConfiguration.fromV1Cookies()
  const savedFeeds = appConfiguration.context === 'PUBLIC' ? loadSavedFeeds() : []
  const [configuration, setConfiguration] = useState(
    defaultFeed(appConfiguration, agencyFeed, urlFeed, v1CookieFeed, savedFeeds)
  )
  useEffect(() => {
    if (appConfiguration.context === 'ADMIN') {
      setConfiguration(agencyFeed)
    }
  }, [sessionAgency?.agencyid])

  useEffect(() => {
    if (appConfiguration.context === 'SANDBOX_LINK') {
      setConfiguration(new FeedConfiguration(undefined, '', [], token, [], 'SANDBOX', 'NEW', false, false, false))
    }
  }, [token])

  useEffect(() => {
    document.addEventListener('keydown', hangleESCKey)

    return () => {
      document.removeEventListener('keydown', hangleESCKey)
    }
  }, [])

  const [loadingAgencies, setLoadingAgencies] = useState(false)
  const [loading, setLoading] = useState(false)
  const [showDisplayOptionDialog, setShowDisplayOptionsDialog] = useState(false)
  const [selectedIncident, setSelectedIncident] = useState<{ agency: AgencyModel; incidentID: string } | undefined>(
    undefined
  )
  const [showSavedFeeds, setShowSavedFeeds] = useState(false)

  const filteredFeedModal = filterCalls(loadedData.feedModel, search, configuration)
  const fetchLatest = (configuration: FeedConfiguration, showLoading: boolean, completion: () => void) => {
    const agencyIDs = configuration.agencyIDs
    showLoading && setLoading(true)
    fetchIncidents(agencyIDs, configuration.token, configuration.showFullPSAP, configuration.environment, session)
      .then(data => {
        setLoadedData({ feedModel: data, agencyIDs: agencyIDs })
        setLoading(false)
        completion()
      })
      .catch(error => {
        completion()
      })
  }

  useEffect(() => {
    if (radioAgency !== null && configuration.agencyIDs.find(agencyID => radioAgency.agencyID === agencyID) == null) {
      audioRef.current?.pause()
      setRadioAgency(null)
    }

    setLoadingAgencies(true)
    fetchAgencies(
      configuration.agencyIDs,
      configuration.token,
      configuration.showFullPSAP,
      configuration.environment,
      session
    )
      .then(loadedAgencies => {
        setAgencies(loadedAgencies)

        if (onAgencyLoaded) {
          loadedAgencies.forEach(agency => {
            onAgencyLoaded(agency)
         })
        }
      })
      .finally(() => setLoadingAgencies(false))
  }, [JSON.stringify(configuration.agencyIDs), configuration.showFullPSAP, configuration.token])

  useEffect(() => {
    mapViewport.current = undefined
    fetchLatest(configuration, true, () => {})
  }, [JSON.stringify(configuration)])

  useEffect(() => {
    const defaultSelectedIncident = incidentFromURL(agencies, searchParams)

    setSelectedIncident(defaultSelectedIncident)
  }, [searchParams, agencies])

  return (
    <ScreenWidthCategoryContext.Provider value={contextProviders.screenWidthCategory}>
      <UpdateToastContext.Provider value={contextProviders.toastManager}>
        <UpdateConfirmationContext.Provider value={contextProviders.confirmationManager}>
          <audio style={{ display: 'none' }} ref={audioRef} />
          {showSavedFeeds ? (
            <ManageSavedFeeds
              didSelectBack={() => setShowSavedFeeds(false)}
              onViewSavedFeed={feed => {
                setShowSavedFeeds(false)
                setConfiguration(feed)
              }}
            />
          ) : feedToConfigure ? (
            <ConfigureFeed
              appConfiguration={appConfiguration}
              configuration={feedToConfigure}
              onConfigureFeed={feed => {
                saveFeedIfNeeded(feed)
                setConfiguration(feed)
                setFeedToConfigure(null)
              }}
              onCancel={() => setFeedToConfigure(null)}
            />
          ) : loadingAgencies ? (
            <PPLoadingDots />
          ) : configuration.isValid() ? (
            selectedIncident ? (
              <IncidentDetails
                appConfiguration={appConfiguration}
                configuration={configuration}
                incidentID={selectedIncident.incidentID}
                agency={selectedIncident.agency}
                session={session}
                onSelectAgency={onSelectAgency}
              />
            ) : (
              <Flex flexDirection="column" flex="1 1 auto" overflowY="auto">
                {showDisplayOptionDialog && (
                  <DisplaySettings
                    data={{ timeDisplay: timeDisplay }}
                    onUpdate={data => {
                      setTimeDisplay(data.timeDisplay)
                      setShowDisplayOptionsDialog(false)
                      localStorage.setItem('TimeDisplayOption', data.timeDisplay)
                    }}
                    onCancel={() => setShowDisplayOptionsDialog(false)}
                  />
                )}
                <RespondMenuBar
                  onTogglePanelSide={() => {
                      const newPanelSide = panelSide === 'left' ? 'right': 'left'
                      localStorage.setItem('IncidentListPanelSide', newPanelSide)
                      setPanelSide(newPanelSide)
                  }}
                  panelSide={panelSide}
                  configuration={configuration}
                  collapsedHeader={collapsedHeader}
                  appConfiguration={appConfiguration}
                  agencies={agencies}
                  radioAgency={radioAgency}
                  tabIndex={selectedTab}
                  onConfigureFeed={() => setFeedToConfigure(configuration)}
                  onUpdateConfiguration={configuration => {
                    saveFeedIfNeeded(configuration)
                    setConfiguration(configuration)
                  }}
                  onRefresh={completion => fetchLatest(configuration, false, completion)}
                  onCreateNewFeed={(savedCurrent, name) => {
                    if (savedCurrent) {
                      const currentFeed = new FeedConfiguration(
                        configuration.id,
                        name,
                        configuration.agencyIDs,
                        configuration.token,
                        configuration.callTypes,
                        configuration.environment,
                        'SAVED',
                        configuration.hideMedical,
                        configuration.hideRecent,
                        false
                      )
                      setConfiguration(currentFeed)
                      saveFeed(currentFeed)
                    }
                    setFeedToConfigure(
                      new FeedConfiguration(undefined, '', [], '', [], 'PRODUCTION', 'NEW', false, false, false)
                    )
                  }}
                  onViewSavedFeeds={() => setShowSavedFeeds(true)}
                  onViewDisplaySettings={() => setShowDisplayOptionsDialog(true)}
                  onHeaderCollapse={() => {
                    setCollapsedHeader(true)
                    contextProviders.toastManager.setMessages(['Press ESC to Show Header.'], [])
                  }}
                  onChangeRadio={agency => {
                    if (radioAgency == agency) {
                      return
                    }

                    if (agency && agency.hasRadio()) {
                      audioRef.current?.pause()
                      audioRef.current?.setAttribute('src', agency.radioURL)
                      audioRef.current?.play()
                      setRadioAgency(agency)
                    } else {
                      audioRef.current?.pause()
                      setRadioAgency(null)
                    }
                  }}
                  onTabChange={index => {
                    setSelectTab(index)
                    if (userLocation && index === 1) {
                      setUserLocation(undefined)
                      mapViewport.current = undefined
                    }
                  }}
                />
                {!filteredFeedModal.hasData() && loading ? (
                  <PPLoadingDots />
                ) : (
                  <Flex flexDirection={panelSide === 'right' ? "row" : "row-reverse"} flex="1 1 auto" overflowY="auto">
                    {(!isSmallScreenWidth || selectedTab === 2) && (
                      <IncidentMap
                        userLocation={userLocation}
                        session={session}
                        isSmallScreenWidth={isSmallScreenWidth}
                        env={configuration.environment}
                        timeDisplayOption={timeDisplay}
                        agencies={agencies}
                        feedData={filteredFeedModal}
                        viewport={mapViewport.current}
                        onSelectIncident={onSelectIncident}
                        onMapMove={viewport => {
                          mapViewport.current = viewport

                          if (!viewport) {
                            fetchLatest(configuration, false, () => {})
                          }
                        }}
                      />
                    )}
                    {(!isSmallScreenWidth || selectedTab === 1) && (
                      <IncidentList
                        collapsedHeader={collapsedHeader}
                        onShowUserLocation={(location) => {
                          setUserLocation(location)
                          mapViewport.current = {
                            center: location
                          }
                          setSelectTab(2)
                        }}
                        timeDisplayOption={timeDisplay}
                        agencies={agencies}
                        feedData={filteredFeedModal}
                        onSelectIncident={onSelectIncident}
                        search={search}
                        setSearch={search => setSearch(search)}
                        isSmallScreenWidth={isSmallScreenWidth}
                        panelSide={panelSide}
                        onTogglePanelSide={() => {
                          const newPanelSide = panelSide === 'left' ? 'right': 'left'
                          localStorage.setItem('IncidentListPanelSide', newPanelSide)
                          setPanelSide(newPanelSide)
                        }}
                      />
                    )}
                  </Flex>
                )}
              </Flex>
            )
          ) : (
            <EmptyState>
              <EmptyState.Heading>Welcome to Respond for Web</EmptyState.Heading>
              <EmptyState.Body style={{maxWidth: '600px'}}>
                Your personalized incident feed will appear here once configured.
                <br /><br /><br />
                To get started, use the button below to select at least one agency to view.
              </EmptyState.Body>
              <PrimaryButton colors={PrimaryButtonColors} onClick={event => setFeedToConfigure(configuration)}>
                Create New Feed
              </PrimaryButton>
            </EmptyState>
          )}
        </UpdateConfirmationContext.Provider>
      </UpdateToastContext.Provider>
    </ScreenWidthCategoryContext.Provider>
  )
}
